import type { ItemType } from './item-types.ts';
import type { PlanMarkerKind } from './schemas/plan-floor.ts';

// The app's inline icon set (24-unit grid, 2px strokes, currentColor):
// path data shared by the React <Icon> and the topology SVG, which is drawn
// on the Worker for reports as well as in the browser.

export const ICON_PATHS = {
  building:
    'M3 21h18M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16M9 7h1m4 0h1M9 11h1m4 0h1M9 15h1m4 0h1M11 21v-3h2v3',
  home: 'M3 11 12 3l9 8M5 10v11h5v-6h4v6h5V10',
  'map-pin': 'M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Zm-5 0a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z',
  door: 'M4 21V4a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v17M3 21h18M15 12h.01M8 3v18',
  rack: 'M5 3h14v18H5zM5 9h14M5 15h14M8 6h.01M8 12h.01M8 18h.01M11 6h5M11 12h5M11 18h5',
  cable:
    'M7 6v4a5 5 0 0 0 5 5 5 5 0 0 1 5 5M7 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm10 16a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  device:
    'M9 3v2m6-2v2M9 19v2m6-2v2M3 9h2m-2 6h2m14-6h2m-2 6h2M7 5h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Zm2 4h6v6H9z',
  wifi: 'M2 9a15 15 0 0 1 20 0M5 12.5a10 10 0 0 1 14 0M8.5 16a5 5 0 0 1 7 0M12 20h.01',
  router:
    'M3 14h18a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1Zm4 3h.01m3 0h.01M12 14V9m-3-3a4 4 0 0 1 6 0M6.5 3.5a8 8 0 0 1 11 0',
  shield: 'M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6l-8-3Zm-3 9 2 2 4-4',
  switch:
    'M4 6h16a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Zm0 8h16a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1Zm3-5.5h.01m3 0h.01m3 0h.01M7 16.5h.01m3 0h.01m3 0h.01',
  camera: 'M4 8h3l2-3h6l2 3h3v11H4zm8 8.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z',
  drive: 'M2 12h20M5.5 6h13l3.5 6v6H2v-6zM6 16h.01m4 0h.01',
  power: 'M13 2 3 14h9l-1 8 10-12h-9z',
  plug: 'M9 2v6m6-6v6M6 8h12v4a6 6 0 0 1-12 0zM12 18v4',
  globe: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm-9-9h18M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18Z',
  cpu: 'M5 5h14v14H5zM9 9h6v6H9zM9 1v4m6-4v4M9 19v4m6-4v4M1 9h4m-4 6h4m14-6h4m-4 6h4',
  server:
    'M4 4h16a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Zm0 10h16a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1Zm3-7h.01M7 17h.01',
  box: 'M21 8 12 3 3 8v8l9 5 9-5zM3 8l9 5 9-5M12 13v8',
  grid: 'M4 4h16v16H4zM4 12h16M12 4v16',
  /** Three lines: a list of rows, beside `grid` on a view picker (W17). */
  list: 'M4 6h16M4 12h16M4 18h16',
  /** Three rows in a frame: a table, beside `grid` on a view picker (W17). */
  table: 'M3 5h18v14H3zM3 10h18M3 15h18M9 10v9',
  panel: 'M3 7h18v10H3zM7 12h.01m3 0h.01m3 0h.01m3 0h.01',
  shelf: 'M3 8h18M3 16h18M5 8v8M19 8v8M8 8v8m8-8v8',
  drawer: 'M3 5h18v14H3zM3 12h18M10 8.5h4m-4 7h4',
  'cable-manager': 'M4 8h16M4 16h16M8 4v16m8-16v16',
  'power-strip': 'M2 9h20v6H2zm4 3h.01m4 0h.01m4 0h.01m4 0h.01M12 15v6',
  /** A faceplate with two slots and an earth pin: the wall outlet (P4). */
  outlet: 'M5 3h14v18H5zM10 8v3m4-3v3M12 15v2',
  /** A faceplate with two keystone jacks: the wall patch panel (P4). */
  'wall-plate': 'M4 4h16v16H4zM8 8h3v4H8zm5 0h3v4h-3zM8 16h8',
  plus: 'M12 5v14M5 12h14',
  'chevron-right': 'm9 18 6-6-6-6',
  'chevron-down': 'm6 9 6 6 6-6',
  'chevron-left': 'm15 18-6-6 6-6',
  x: 'M18 6 6 18M6 6l12 12',
  'arrow-left': 'M19 12H5m7 7-7-7 7-7',
  'arrow-up': 'M12 19V5m-7 7 7-7 7 7',
  'arrow-down': 'M12 5v14m7-7-7 7-7-7',
  pencil: 'M17 3a2.8 2.8 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3Z',
  trash: 'M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6m4-6v6',
  /** An arrow turning back on itself: restore, revert. */
  undo: 'M3 7v6h6M3 13a9 9 0 1 0 2.6-6.4L3 9',
  more: 'M5 12h.01M12 12h.01M19 12h.01',
  'log-out': 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4m7 14 5-5-5-5m5 5H9',
  settings:
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm7.4-3a7.4 7.4 0 0 0-.1-1l2.1-1.6-2-3.4-2.5 1a7.6 7.6 0 0 0-1.7-1L14.8 3H9.2l-.4 2.6a7.6 7.6 0 0 0-1.7 1l-2.5-1-2 3.4L4.7 11a7.4 7.4 0 0 0 0 2l-2.1 1.6 2 3.4 2.5-1a7.6 7.6 0 0 0 1.7 1l.4 2.6h5.6l.4-2.6a7.6 7.6 0 0 0 1.7-1l2.5 1 2-3.4-2.1-1.6c.1-.3.1-.7.1-1Z',
  sun: 'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0-15v2m0 16v2M4.2 4.2l1.4 1.4m12.8 12.8 1.4 1.4M2 12h2m16 0h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4',
  moon: 'M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z',
  monitor: 'M3 4h18v12H3zM8 20h8m-4-4v4',
  /** An upright box with a drive slot and a power button: the desktop computer (P4). */
  tower:
    'M7 2h10a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1Zm3 4h4m-4 3h4m2 7a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z',
  laptop: 'M5 5h14v10H5zM2 18h20l-2-3H4z',
  converter: 'M2 12h5m10 0h5M7 8h10v8H7zM10 12h4M12 2v3m0 14v3',
  speaker: 'M6 4h12v16H6zM12 9.5a3 3 0 1 0 0 6 3 3 0 0 0 0-6Zm0-2.5h.01',
  amplifier: 'M3 7h18v10H3zM6 10h4m-4 3h4M15 12a2 2 0 1 0 4 0 2 2 0 0 0-4 0Z',
  /** A wide box with a display and two source buttons: the AV receiver (P4). */
  receiver: 'M2 7h20v10H2zM5 10h5v4H5zM14 12h.01M18 12h.01M14 15h5',
  projector: 'M3 8h14a3 3 0 0 1 0 8H3zM6 19v2m10-2v2M20 10h1m-1 4h1M12 12h.01',
  keypad: 'M6 3h12v18H6zM9 7h.01m3 0h.01m3 0h.01M9 11h.01m3 0h.01m3 0h.01M9 15h.01m3 0h.01m3 0h.01',
  reader: 'M7 3h10v18H7zM10 7h4M9 12h6M11 16h2',
  strike: 'M8 11h8v9H8zM10 11V7a2 2 0 0 1 4 0v4M12 14v3',
  siren: 'M6 20h12M8 20v-6a4 4 0 0 1 8 0v6M12 4v3M5 9l2 1m12-1-2 1M4 15H2m20 0h-2',
  /** A wall box with a bell inside it: the intrusion panel (P4). */
  'alarm-panel': 'M4 4h16v16H4zM12 8a3 3 0 0 0-3 3v2l-1 1h8l-1-1v-2a3 3 0 0 0-3-3Zm-1 7h2',
  flame: 'M12 3s5 4 5 9a5 5 0 0 1-10 0c0-2 1-3.5 2-4.5.5 2 1.5 2.5 2 2 .5-.5 1-2 1-3.5Z',
  thermostat: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm0 6a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-9V7',
  sensor:
    'M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM7.5 7.5a6 6 0 0 0 0 9M16.5 7.5a6 6 0 0 1 0 9M4 4a11 11 0 0 0 0 16M20 4a11 11 0 0 1 0 16',
  splitter: 'M3 12h5l4-5h9M8 12l4 5h9M6 12a2 2 0 1 0 4 0 2 2 0 0 0-4 0Z',
  'printer-3d': 'M4 4h16v10H4zM8 14v3l4 2 4-2v-3M12 19v3M9 8h6',
  image: 'M3 5h18v14H3zm5 6a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Zm13 4-5-5L5 21',
  'file-text':
    'M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9l-6-6Zm0 0v6h6M8 13h8m-8 4h8',
  download: 'M12 3v12m-5-5 5 5 5-5M4 21h16',
  upload: 'M12 15V3m-5 5 5-5 5 5M4 21h16',
  external: 'M14 3h7v7m0-7-9 9M10 5H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5',
  move: 'M5 9 2 12l3 3M9 5l3-3 3 3m0 14-3 3-3-3m10-10 3 3-3 3M2 12h20M12 2v20',
  search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14Zm10 3-4.3-4.3',
  check: 'M20 6 9 17l-5-5',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-9v5m0-9h.01',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-14v5l3 2',
  layers: 'm12 2 10 5-10 5L2 7l10-5Zm-10 10 10 5 10-5M2 17l10 5 10-5',
  grip: 'M9 6h.01M15 6h.01M9 12h.01M15 12h.01M9 18h.01M15 18h.01',
  copy: 'M8 8h12v12H8zM16 8V4H4v12h4',
  filter: 'M3 5h18l-7 8v6l-4-2v-4z',
  link: 'M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1',
  activity: 'M3 12h4l3-8 4 16 3-8h4',
  user: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z',
  phone:
    'M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2',
  mail: 'M3 5h18v14H3zm0 2 9 6 9-6',
  star: 'm12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2L12 17.3 6.4 20.2l1.1-6.2L3 9.6l6.2-.9L12 3Z',
  key: 'M14 4a6 6 0 1 0 0 12 6 6 0 0 0 0-12Zm0 3h.01M10 14l-7 7m3-3 2 2m1-5 2 2',
  printer: 'M6 9V3h12v6M6 18H3v-7h18v7h-3M6 14h12v7H6z',
  lock: 'M5 11h14v10H5zM8 11V7a4 4 0 0 1 8 0v4',
  eye: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Zm10 3a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
  'eye-off':
    'M3 3l18 18M10.6 10.6A3 3 0 0 0 13.4 13.4M9.9 5.2A10.4 10.4 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3.2 4M6.6 6.6C3.9 8.5 2 12 2 12s3.5 7 10 7a9.7 9.7 0 0 0 4.4-1',
  terminal: 'M4 17l6-5-6-5M12 19h8',
  ruler: 'M3 8h18v8H3zM7 8v3m4-3v4m4-4v3m4-3v4',
  /** A flag on a pole: the punch pin's glyph (ROADMAP D8). */
  flag: 'M5 22V3m0 0h12l-2.5 4L17 11H5',
  /** A hand-drawn stroke: the freehand line markup (D8). */
  scribble: 'M3 17c3-6 5 4 8-2s4 3 7-3',
  /** An arrow pointing up-right: the arrow markup (D8). */
  'arrow-up-right': 'M7 17 17 7M9 7h8v8',
  /** A scalloped ring: the revision cloud markup (D8). */
  cloud: 'M7 18a4 4 0 0 1-.5-8 5 5 0 0 1 9.4-1.5A3.5 3.5 0 0 1 17.5 18H7Z',
} as const;

export type IconName = keyof typeof ICON_PATHS;

/**
 * The glyph each device type wears. No two types share one (ROADMAP P4, and
 * `icons.test.ts` holds it): a screenshot of a rack or a map has to say what
 * each box is without its label, so a firewall and an intrusion panel cannot
 * both be a shield, nor a PDU and a wall outlet both a plug.
 */
export const ITEM_ICONS: Readonly<Record<ItemType, IconName>> = {
  access_point: 'wifi',
  router: 'router',
  firewall: 'shield',
  switch: 'switch',
  camera: 'camera',
  nvr: 'drive',
  ups: 'power',
  pdu: 'plug',
  modem_ont: 'globe',
  media_converter: 'converter',
  splitter: 'splitter',
  controller: 'cpu',
  server_nas: 'server',
  computer: 'tower',
  laptop: 'laptop',
  screen: 'monitor',
  printer_3d: 'printer-3d',
  access_control_panel: 'lock',
  reader: 'reader',
  door_strike: 'strike',
  intrusion_panel: 'alarm-panel',
  fire_panel: 'flame',
  horn_strobe: 'siren',
  amplifier: 'amplifier',
  receiver: 'receiver',
  projector: 'projector',
  speaker: 'speaker',
  keypad: 'keypad',
  thermostat: 'thermostat',
  sensor: 'sensor',
  phone: 'phone',
  printer: 'printer',
  wall_patch_panel: 'wall-plate',
  outlet: 'outlet',
  other: 'box',
  patch_panel: 'panel',
  blank_panel: 'grid',
  shelf: 'shelf',
  drawer: 'drawer',
  cable_manager: 'cable-manager',
  power_strip: 'power-strip',
};

/** The glyph for a device or rack part. */
export function itemIcon(type: ItemType): IconName {
  return ITEM_ICONS[type];
}

/**
 * The glyph a floor-plan marker wears, by the kind it draws. Single-sourced
 * here so the drawing palette, the on-screen canvas, and the server-side
 * report drawing all wear the same icon for an outlet, a camera, and the rest.
 */
export const PLAN_MARKER_ICONS: Readonly<Record<PlanMarkerKind, IconName>> = {
  outlet: 'outlet',
  camera: 'camera',
  ap: 'wifi',
  drop: 'cable',
  device: 'device',
  punch: 'flag',
};
